import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, Table } from '../../components/ui.jsx';
import { Select } from '../../components/controls/Select.jsx';

// ---------------------------------------------------------------------------
// PLACEHOLDER TAB — nothing here is real.
//
// Client Billing needs a finance workflow plus technical-telemetry feeds
// (Elastic / platform usage) that do not exist yet. This tab exists only to
// show the intended shape to stakeholders. Every figure below is invented and
// marked SAMPLE. There is no backend, no persistence and no write path.
// ---------------------------------------------------------------------------

const MONTHS = ['July 2026', 'June 2026', 'May 2026', 'April 2026'];

const SUMMARY = [
  ['Contracted / recurring', '18,750 USD'],
  ['Transactional', '3,120 USD'],
  ['One-time', '0 USD'],
  ['Expected invoice', '21,870 USD'],
  ['Technical spend', '2,940 USD'],
  ['People effort', '14.6 MD'],
];

const LINE_ITEMS = [
  [
    'Fixed services',
    'Fixed recurring',
    '1 month',
    '12,000 USD',
    '12,000 USD',
    'Confirmed',
  ],
  [
    'Licence',
    'Fixed recurring',
    '30 licences',
    '150 USD',
    '4,500 USD',
    'Confirmed',
  ],
  [
    'Support',
    'Fixed recurring',
    '1 month',
    '2,250 USD',
    '2,250 USD',
    'Confirmed',
  ],
  [
    'Voice usage',
    'Usage based',
    'Waiting for usage data',
    '0.018 USD / min',
    '—',
    'Waiting for data',
  ],
  [
    'AI interaction',
    'Usage based',
    'Waiting for usage data',
    '0.04 USD / interaction',
    '—',
    'Waiting for data',
  ],
  ['Phone numbers', 'Usage based', '12 numbers', '2 USD', '24 USD', 'Ready'],
];

const CHECKS = [
  ['Recurring items confirmed', 'ok'],
  ['Licence & Support unchanged from setup', 'ok'],
  ['Voice usage not yet received', 'warn'],
  ['Technical usage data received', 'warn'],
  ['No missing mandatory items', 'ok'],
];

const TREND = [
  ['Apr 2026', '20,100 USD', '4,020 USD', '2,610 USD', '6,630 USD'],
  ['May 2026', '21,300 USD', '4,560 USD', '2,780 USD', '7,340 USD'],
  ['Jun 2026', '20,880 USD', '4,380 USD', '2,900 USD', '7,280 USD'],
  ['Jul 2026', '21,870 USD', '4,380 USD', '2,940 USD', '7,320 USD'],
];

const TECH_COST = [
  ['LLM', '910 USD'],
  ['STT', '640 USD'],
  ['TTS', '520 USD'],
  ['PBX / Telephony', '470 USD'],
  ['Infrastructure', '400 USD'],
];

const Sample = () => <span className="sample-pill">Sample</span>;

export function ClientBillingTab({ client }) {
  return (
    <div className="client-billing-tab">
      <div className="calendar-connect-hint warning placeholder-banner">
        <AlertTriangle size={16} />
        <div>
          <b>Placeholder — not real data.</b> Client Billing needs a finance
          approval workflow and technical-usage telemetry that aren&rsquo;t
          integrated yet. Every figure below is invented so the team can review
          the intended layout.
        </div>
      </div>

      <div className="billing-month-row">
        <label>
          <span>Billing month</span>
          <Select
            ariaLabel="Billing month"
            value={MONTHS[0]}
            onChange={() => {}}
            disabled
            options={MONTHS.map((m) => ({ value: m, label: m }))}
          />
        </label>
        <span className="project-status-badge status-paused">
          Ready for review
        </span>
        <span className="muted-text">
          {client?.name} · workflow: Draft → Ready for review → Ready for
          invoice → Invoiced
        </span>
      </div>

      <Card
        title={
          <>
            Monthly billing summary <Sample />
          </>
        }
      >
        <div className="stats stats-five billing-summary">
          {SUMMARY.map(([label, value]) => (
            <div className="stat" key={label}>
              <span className="stat-label">{label}</span>
              <strong className="stat-value">{value}</strong>
            </div>
          ))}
        </div>
      </Card>

      <Card
        title={
          <>
            Billing line items <Sample />
          </>
        }
      >
        <Table
          headers={[
            'Line item',
            'Billing model',
            'Usage / quantity',
            'Rate',
            'Amount (USD)',
            'Status',
          ]}
        >
          {LINE_ITEMS.map((row) => (
            <tr key={row[0]}>
              {row.map((cell, i) => (
                <td key={i} className={i === 4 ? 'strong' : ''}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </Table>
        <p className="muted-text billing-rule">
          Pre-fill rule: fixed recurring items are copied forward; usage-based
          items show the previous month only and wait for real usage before a
          value appears.
        </p>
      </Card>

      <Card
        title={
          <>
            Checks before &ldquo;Mark ready for invoice&rdquo; <Sample />
          </>
        }
      >
        <ul className="billing-checks">
          {CHECKS.map(([label, tone]) => (
            <li key={label} className={tone === 'warn' ? 'is-warn' : 'is-ok'}>
              <span aria-hidden="true">{tone === 'warn' ? '!' : '✓'}</span>
              {label}
            </li>
          ))}
        </ul>
      </Card>

      <div className="billing-two-col">
        <Card
          title={
            <>
              Revenue &amp; cost trend <Sample />
            </>
          }
        >
          <Table
            headers={[
              'Month',
              'Invoiced',
              'People cost',
              'Technical',
              'Total cost',
            ]}
          >
            {TREND.map((row) => (
              <tr key={row[0]}>
                {row.map((cell, i) => (
                  <td key={i} className={i === 0 ? 'strong' : ''}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </Table>
          <p className="muted-text billing-rule">
            People cost = MD × 300 USD (estimate). No profit or margin is
            calculated.
          </p>
        </Card>

        <Card
          title={
            <>
              Monthly cost breakdown <Sample />
            </>
          }
        >
          <dl className="playbook">
            {TECH_COST.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </React.Fragment>
            ))}
            <dt>People effort</dt>
            <dd>14.6 MD · ~4,380 USD</dd>
            <dt>Estimated total</dt>
            <dd className="strong">7,320 USD</dd>
          </dl>
          <p className="muted-text billing-rule">
            Technical cost is what servicing the client costs us — it is not a
            billing line item.
          </p>
        </Card>
      </div>
    </div>
  );
}
